import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isValidHref, isInternalLink } from '../../assets/utils/sidebar/sidebarUtils';

/**
 * 사이드바 서브메뉴 - 트리뷰 스타일
 * 원본: sub-sidebar-menu-tree.html
 *
 * data 구조: { title, menu[], activeHref }
 * menu 구조: [{ label, href?, children[] }]
 */
const SubSidebarMenuTree = ({ data, position = 'left' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);
  const treeContainerRef = useRef(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [sidebarData, setSidebarData] = useState(null);
  const uidRef = useRef(`tree-${Math.random().toString(36).slice(2, 8)}`);

  // 데이터 초기화 및 location.pathname 변경 감지
  useEffect(() => {
    // window.SidebarMenuData를 우선적으로 사용하되, 없으면 data prop 사용
    const sidebarMenuData = window.SidebarMenuData || data || { title: '', menu: [], activeHref: '' };

    // location.pathname이 변경되었을 때 activeHref 업데이트
    const updatedData = {
      ...sidebarMenuData,
      activeHref: location.pathname
    };

    setSidebarData(updatedData);
  }, [data, location.pathname]);

  // 초기 확장 상태 설정 (active 항목이 있는 그룹은 자동 확장)
  useEffect(() => {
    if (!sidebarData) return;

    // location.pathname을 우선적으로 사용하여 항상 최신 경로 반영
    const activeHref = location.pathname;
    const groups = Array.isArray(sidebarData.menu) ? sidebarData.menu : [];
    const newExpandedItems = new Set();
    const uid = uidRef.current;

    // 그룹에 active 항목이 있는지 확인 (원본 hasActiveItem 로직과 동일)
    const hasActiveItem = (group) => {
      if (!group) return false;
      // 그룹 자체가 active인지 확인
      const groupHref = group?.href;
      const hasValidGroupHref = isValidHref(groupHref);
      if (hasValidGroupHref && groupHref === activeHref) {
        return true;
      }
      // children 중에 active가 있는지 확인
      const children = Array.isArray(group?.children) ? group.children : [];
      return children.some((child) => child && child.href === activeHref);
    };

    // 각 그룹에 대해 active 항목이 있으면 확장
    groups.forEach((group, index) => {
      const itemId = `${uid}-item-${index}`;
      if (hasActiveItem(group)) {
        newExpandedItems.add(itemId);
      }
    });

    setExpandedItems(newExpandedItems);
  }, [sidebarData, location.pathname]);

  // 트리 노드 토글 (useCallback으로 메모이제이션)
  const toggleTreeNode = useCallback((itemId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // 링크 클릭 핸들러 (useCallback으로 메모이제이션)
  const handleLinkClick = useCallback((e, href) => {
    if (!isValidHref(href)) {
      e.preventDefault();
      return;
    }

    if (isInternalLink(href)) {
      e.preventDefault();
      e.stopPropagation();

      // 현재 활성화된 메뉴 항목을 다시 클릭한 경우 업무화면 영역만 새로고침
      const currentPath = location.pathname;
      if (href === currentPath) {
        // 전역 함수를 통해 페이지 데이터만 다시 로드
        if (typeof window.reloadPageData === 'function') {
          window.reloadPageData();
        }
        return;
      }

      navigate(href);
    }
    // 외부 링크는 기본 동작 허용
  }, [navigate, location.pathname]);

  // 사이드바 토글 초기화
  useEffect(() => {
    if (!rootRef.current) return;

    const sidebarRoot = rootRef.current.querySelector('.sidebar-root') || rootRef.current;

    if (typeof window.SidebarMenuCommon !== 'undefined' &&
        typeof window.SidebarMenuCommon.initSidebarToggle === 'function') {
      window.SidebarMenuCommon.initSidebarToggle(sidebarRoot);
    } else if (typeof window.initSidebarToggle === 'function') {
      window.initSidebarToggle(sidebarRoot);
    }

    if (typeof window.initSidebarScrollOffset === 'function') {
      window.initSidebarScrollOffset();
    }

    if (typeof window.AOS !== 'undefined') {
      requestAnimationFrame(() => {
        window.AOS.refreshHard();
      });
    }
  }, [sidebarData]);

  // 트리 항목 렌더링 (useCallback으로 메모이제이션)
  const renderTreeItem = useCallback((group, depth, parentExpanded, itemId) => {
    if (!group) return null;

    const children = Array.isArray(group?.children) ? group.children : [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedItems.has(itemId);
    const groupHref = group?.href;
    const hasValidGroupHref = isValidHref(groupHref);
    // location.pathname을 우선적으로 사용하여 항상 최신 경로 반영
    const activeHref = location.pathname;
    // 자식이 active인 경우(정확히 일치)에는 부모까지 active 처리하지 않음
    // (부모/자식 href가 동일하게 들어오는 데이터 케이스에서 중복 하이라이트 방지)
    const isChildActiveInGroup = hasChildren && children.some((c) => isValidHref(c?.href) && c.href === activeHref);
    const isGroupActive = hasValidGroupHref && groupHref === activeHref && !isChildActiveInGroup;

    return (
      <React.Fragment key={itemId}>
        <div className="tree-menu-item" data-depth={depth}>
          {/* 토글 버튼 */}
          {hasChildren ? (
            <button
              type="button"
              className="tree-toggle-btn"
              data-toggle-id={itemId}
              aria-expanded={isExpanded}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleTreeNode(itemId);
              }}
            >
              <span className={`tree-toggle-sign ${isExpanded ? 'expanded' : ''}`}>
                {isExpanded ? '−' : '+'}
              </span>
            </button>
          ) : (
            <span className="tree-toggle-spacer"></span>
          )}

          {/* 링크 또는 텍스트 */}
          <div className={`tree-menu-content ${isGroupActive ? 'active' : ''}`}>
            {hasValidGroupHref ? (
              isInternalLink(groupHref) ? (
                <a
                  href={groupHref}
                  className="tree-menu-link"
                  onClick={(e) => handleLinkClick(e, groupHref)}
                >
                  <span className="tree-menu-label">{group?.label || ''}</span>
                </a>
              ) : (
                <a href={groupHref} className="tree-menu-link">
                  <span className="tree-menu-label">{group?.label || ''}</span>
                </a>
              )
            ) : (
              <span className="tree-menu-label">{group?.label || ''}</span>
            )}
          </div>
        </div>

        {/* children 렌더링 */}
        {hasChildren && (
          <div
            className={`tree-menu-children ${isExpanded ? 'expanded' : ''}`}
            id={itemId}
          >
            {children.map((child, childIdx) => {
              const childHref = child?.href;
              const hasValidChildHref = isValidHref(childHref);
              const isChildActive = hasValidChildHref && childHref === activeHref;
              const count = child?.count !== undefined ? child.count : '';

              return (
                <div
                  key={childIdx}
                  className="tree-menu-item tree-menu-child-item"
                  data-depth={depth + 1}
                  data-parent-id={itemId}
                >
                  <span className="tree-toggle-spacer"></span>
                  <div className={`tree-menu-content ${isChildActive ? 'active' : ''}`}>
                    {hasValidChildHref ? (
                      isInternalLink(childHref) ? (
                        <a
                          href={childHref}
                          className="tree-menu-link"
                          onClick={(e) => handleLinkClick(e, childHref)}
                        >
                          <span className="tree-menu-label">{child?.label || ''}</span>
                          {count !== '' && (
                            <span className="tree-menu-count">{count}</span>
                          )}
                        </a>
                      ) : (
                        <a href={childHref} className="tree-menu-link">
                          <span className="tree-menu-label">{child?.label || ''}</span>
                          {count !== '' && (
                            <span className="tree-menu-count">{count}</span>
                          )}
                        </a>
                      )
                    ) : (
                      <>
                        <span className="tree-menu-label">{child?.label || ''}</span>
                        {count !== '' && (
                          <span className="tree-menu-count">{count}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </React.Fragment>
    );
  }, [expandedItems, location.pathname, toggleTreeNode, handleLinkClick]);

  // 이벤트 위임 방식으로 트리 컨테이너에 클릭 핸들러 등록 (원본과 동일)
  useEffect(() => {
    if (!treeContainerRef.current) return;

    const treeContainer = treeContainerRef.current;

    // 기존 이벤트 리스너 제거
    const existingHandler = treeContainer._treeToggleHandler;
    if (existingHandler) {
      treeContainer.removeEventListener('click', existingHandler);
      treeContainer._treeToggleHandler = null;
    }

    // 이벤트 위임: 컨테이너에 단일 이벤트 리스너 등록 (원본과 동일)
    const clickHandler = (e) => {
      // 토글 버튼 클릭
      const toggleBtn = e.target.closest('.tree-toggle-btn');
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        const toggleId = toggleBtn.getAttribute('data-toggle-id');
        if (toggleId) {
          toggleTreeNode(toggleId);
        }
        return;
      }

      // 제목 영역 클릭 (children이 있는 경우에만) - 원본과 동일
      const contentEl = e.target.closest('.tree-menu-content');
      if (contentEl) {
        const treeItem = contentEl.closest('.tree-menu-item');
        if (treeItem) {
          const toggleBtn = treeItem.querySelector('.tree-toggle-btn');
          if (toggleBtn) {
            // 링크가 있는 경우, 링크 클릭 시에는 기본 동작 허용
            const link = e.target.closest('.tree-menu-link');
            if (link && link.getAttribute('href') && link.getAttribute('href') !== '#' && link.getAttribute('href') !== '') {
              // 유효한 링크가 있으면 기본 동작 허용 (페이지 이동)
              return;
            }
            // 링크가 없거나 유효하지 않은 경우 토글 동작
            e.preventDefault();
            e.stopPropagation();
            const toggleId = toggleBtn.getAttribute('data-toggle-id');
            if (toggleId) {
              toggleTreeNode(toggleId);
            }
          }
        }
      }
    };

    // 이벤트 리스너 등록 및 핸들러 참조 저장
    treeContainer.addEventListener('click', clickHandler);
    treeContainer._treeToggleHandler = clickHandler;

    return () => {
      if (treeContainer._treeToggleHandler) {
        treeContainer.removeEventListener('click', treeContainer._treeToggleHandler);
        treeContainer._treeToggleHandler = null;
      }
    };
  }, [sidebarData, toggleTreeNode]);

  if (!sidebarData) return null;

  const groups = Array.isArray(sidebarData.menu) ? sidebarData.menu : [];
  const uid = uidRef.current;

  return (
    <div
      ref={rootRef}
      className="sidebar-menu sidebar-menu-tree"
      data-aos={position === 'right' ? 'fade-left' : 'fade-right'}
      data-aos-delay="100"
    >
      <div className="sidebar-root">
        <div className="sidebar-title-wrapper">
          <h5 className="sidebar-title">
            <span className="sidebar-title-bar"></span>
            <span className="sidebar-title-text">{sidebarData.title || '섹션 제목'}</span>
            <button
              type="button"
              className="sidebar-toggle-btn"
              id="sidebar-toggle-btn"
              aria-label="사이드바 접기/펼치기"
            >
              <i className="ti-angle-left sidebar-toggle-icon"></i>
            </button>
          </h5>
        </div>
        <div className="tree-menu-container" ref={treeContainerRef} id={uid}>
          {groups.map((group, index) => {
            // 원본과 동일하게 안정적인 ID 생성 (index 기반)
            const itemId = `${uid}-item-${index}`;
            return renderTreeItem(group, 0, true, itemId);
          })}
        </div>
        <div className="sidebar-title-line"></div>
      </div>
    </div>
  );
};

export default SubSidebarMenuTree;

